import { Button, Card, RadixTable, TColumn } from '@features/ui';
import { useEffect, useMemo, useState } from 'react';
import { SaleTaxRate } from '@src/types/feeSettings.ts';
import { toast } from 'react-toastify';
import {
  getSaleTaxRate,
  createSaleTaxRate,
  updateSaleTaxRate,
  deleteSaleTaxRate,
} from '@src/services/taxAndFee';

import {
  CreateEditTaxModal,
  CreateEditTaxFormValues,
} from './components/CreateEditTaxModal';

export const DomesticTax = () => {
  const [saleTaxRate, setSaleTaxRate] = useState<SaleTaxRate[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTax, setEditTax] = useState<SaleTaxRate | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleGetSaleTaxRate = async () => {
    try {
      const res = await getSaleTaxRate();

      setSaleTaxRate(res.data);
    } catch (error) {
      toast.error('Failed to fetch sale tax rate. Please try again.');
      console.error('Error fetching sale tax rate:', error);
    }
  };

  const handleEditItem = (id?: number) => {
    const found = saleTaxRate.find((item) => item.id === id);

    if (found) {
      setEditTax(found);
      setModalOpen(true);
    }
  };

  const handleDeleteItem = (id?: number) => {
    setDeleteId(id ?? null);
    setConfirmDeleteOpen(true);
  };

  const handleAddNewItem = () => {
    setEditTax(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditTax(null);
  };

  const handleModalSubmit = async (values: CreateEditTaxFormValues) => {
    setLoading(true);
    try {
      const payload = {
        country: values.country,
        state: values.name,
        name: values.name,
        rate: values.rate,
      };

      if (editTax) {
        await updateSaleTaxRate(editTax.id, payload);
        toast.success('Tax updated successfully');
      } else {
        await createSaleTaxRate(payload);
        toast.success('Tax created successfully');
      }
      handleGetSaleTaxRate();
      setModalOpen(false);
      setEditTax(null);
    } catch (error) {
      toast.error('Failed to save tax. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await deleteSaleTaxRate(deleteId);
      toast.success('Tax deleted successfully');
      handleGetSaleTaxRate();
    } catch (error) {
      toast.error('Failed to delete tax. Please try again.');
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
      setDeleteId(null);
    }
  };

  const columns = useMemo(
    (): TColumn<SaleTaxRate>[] => [
      {
        title: 'Country',
        render: (item) => <p>{item?.country}</p>,
      },
      {
        title: 'State',
        render: (item) => <p>{item?.name}</p>,
      },
      {
        title: 'Tax Value',
        render: (item) => <p>{item?.rate}%</p>,
      },
      {
        title: 'Action',
        render: (item) => (
          <div className="flex gap-2">
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => handleEditItem(item?.id)}
            >
              Edit
            </Button>
            <Button
              size={'sm'}
              variant={'destructive'}
              onClick={() => handleDeleteItem(item?.id)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [saleTaxRate]
  );

  useEffect(() => {
    handleGetSaleTaxRate();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className={'text-[23px]'}>Set Up Domestic Sales Tax Rates</h1>
        <Button size={'sm'} variant={'lime'} onClick={handleAddNewItem}>
          Add New
        </Button>
      </div>
      <Card className={'text-xs mt-[18px]'}>
        <RadixTable columns={columns} data={saleTaxRate} />
      </Card>
      <CreateEditTaxModal
        initialValues={
          editTax ? { ...editTax, rate: editTax.rate * 100 } : undefined
        }
        isEdit={!!editTax}
        loading={loading}
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />
      {confirmDeleteOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30">
          <div className="bg-background rounded-lg p-6 min-w-[320px] shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
            <p className="mb-6">
              Are you sure you want to delete this tax config?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmDeleteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                Yes, Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
